import { css } from '@understory-io/leaf/css';
import { cx } from '~/styled-system/css';

/**
 * Read-only stars, one or a row of five.
 *
 * Not the `reviewStar` recipe the review form uses — that one is an input,
 * sized to be clicked — but coloured from the same token, so a guest sees the
 * same star reporting a rating as they clicked to leave one. The Leaf provider
 * rewrites `--colors-interactive-accent-*` from the company's primaryColor, so
 * the stars carry the brand of the site they are shown on.
 */

const STAR_COUNT = 5;

// The design's star is Material's, on a 24 viewBox. One path scaled to each
// size rather than an asset per size: the 20px and 24px exports are the same
// glyph, and scaling one keeps them identical.
const STAR_PATH =
  'M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z';

export function ReviewStar({
  filled,
  size,
}: {
  filled: boolean;
  size: number;
}) {
  return (
    <svg
      className={cx(starStyles, filled ? filledStyles : emptyStyles)}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d={STAR_PATH} fill="currentColor" />
    </svg>
  );
}

export function ReviewStars({
  rating,
  size,
}: {
  /** Out of five. Rounded to whole stars — the printed number carries the rest. */
  rating: number;
  /** Edge of each star's box, in pixels. */
  size: number;
}) {
  const filledTo = Math.round(rating);

  return (
    // The rating is printed beside every row of these, so the stars repeat what
    // a reader already has and are hidden rather than read out twice.
    <span className={rowStyles} aria-hidden="true">
      {Array.from({ length: STAR_COUNT }, (_, index) => (
        <ReviewStar key={index} filled={index < filledTo} size={size} />
      ))}
    </span>
  );
}

const rowStyles = css({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
});

const starStyles = css({
  display: 'block',
  flexShrink: 0,
});

const filledStyles = css({ color: 'interactive.accent.default' });
const emptyStyles = css({ color: 'surface.neutral' });
